import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";
import { Select } from "@normasteaching/norma-ui";
import { Card } from "../UI/Card.tsx";
import { SegmentedControl } from "../UI/SegmentedControl.tsx";
import { InputWithLabel } from "../UI/InputWithLabel.tsx";
import { TextInput } from "../UI/TextInput.tsx";
import { PrimaryButton } from "../UI/PrimaryButton.tsx";
import { useToast } from "../UI/Feedback/useToast.ts";
import { WeekDayEnum } from "../gql/graphql.ts";
import { ConductorSelect } from "./ConductorSelect.tsx";
import {
  bookableHoursOf,
  formatHour,
  toStartUtc,
  UserGroupAvailability,
  weekDayOf,
  weekDaysInOrder,
} from "./onboardingLiveTime.ts";

enum AddSlotsMode {
  Single = "Single",
  Recurring = "Recurring",
}

const dateFormat = "YYYY-MM-DD";

function SingleSlotForm({
  availability,
  onAdd,
}: {
  availability?: UserGroupAvailability | null;
  onAdd: (slot: { startUtc: string; teacherId?: string }) => Promise<void>;
}) {
  const [date, setDate] = useState(dayjs().add(1, "day").format(dateFormat));
  const [hour, setHour] = useState<number>();
  const [teacherId, setTeacherId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const hours = bookableHoursOf(availability, [weekDayOf(date)]);

  async function add() {
    if (hour === undefined) return;
    setAdding(true);
    try {
      await onAdd({ startUtc: toStartUtc(date, hour), teacherId });
      setHour(undefined);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className={"grid grid-cols-1 md:grid-cols-4 gap-3 items-end"}>
      <InputWithLabel label={"Giorno"}>
        <TextInput
          type={"date"}
          value={date}
          min={dayjs().format(dateFormat)}
          onChange={(e) => {
            setDate(e.target.value);
            setHour(undefined);
          }}
        />
      </InputWithLabel>
      <InputWithLabel label={"Orario (fasce del gruppo)"}>
        <Select<string>
          placeholder={hours.length > 0 ? "Seleziona" : "Nessuna fascia"}
          disabled={hours.length <= 0}
          options={hours.map((h) => ({ id: `${h}`, name: formatHour(h) }))}
          value={hour === undefined ? undefined : `${hour}`}
          onChange={(v) => setHour(v === null ? undefined : Number(v))}
        />
      </InputWithLabel>
      <InputWithLabel label={"Conduttore"}>
        <ConductorSelect value={teacherId} onChange={setTeacherId} />
      </InputWithLabel>
      <PrimaryButton
        label={"Aggiungi slot"}
        loading={adding}
        disabled={hour === undefined}
        onClick={add}
      />
    </div>
  );
}

function RecurringSlotsForm({
  availability,
  onAdd,
}: {
  availability?: UserGroupAvailability | null;
  onAdd: (recurrence: {
    weekDays: WeekDayEnum[];
    startTimes: string[];
    fromDate: string;
    toDate: string;
    teacherId?: string;
  }) => Promise<void>;
}) {
  const [weekDays, setWeekDays] = useState<WeekDayEnum[]>([]);
  const [hours, setHours] = useState<number[]>([]);
  const [fromDate, setFromDate] = useState(
    dayjs().add(1, "day").format(dateFormat),
  );
  const [toDate, setToDate] = useState(
    dayjs().add(4, "week").format(dateFormat),
  );
  const [teacherId, setTeacherId] = useState<string>();
  const [adding, setAdding] = useState(false);
  const availableHours = bookableHoursOf(
    availability,
    weekDays.length > 0 ? weekDays : weekDaysInOrder.map((w) => w.weekDay),
  );
  const slotsCount = useMemo(() => {
    let count = 0;
    for (
      let day = dayjs(fromDate);
      !day.isAfter(dayjs(toDate), "day");
      day = day.add(1, "day")
    )
      if (weekDays.includes(weekDayOf(day.format(dateFormat))))
        count += hours.length;
    return count;
  }, [fromDate, toDate, weekDays, hours]);

  function toggle<T>(values: T[], value: T): T[] {
    return values.includes(value)
      ? values.filter((v) => v !== value)
      : [...values, value];
  }

  async function add() {
    setAdding(true);
    try {
      await onAdd({
        weekDays,
        startTimes: hours.map(formatHour),
        fromDate,
        toDate,
        teacherId,
      });
      setWeekDays([]);
      setHours([]);
    } finally {
      setAdding(false);
    }
  }

  return (
    <div className={"space-y-4"}>
      <InputWithLabel label={"Giorni della settimana"}>
        <div className={"flex flex-wrap gap-2"}>
          {weekDaysInOrder.map(({ weekDay, label }) => (
            <label
              key={weekDay}
              className={
                "flex items-center gap-1 rounded border px-2 py-1 text-sm"
              }
            >
              <input
                type={"checkbox"}
                checked={weekDays.includes(weekDay)}
                onChange={() => setWeekDays((prev) => toggle(prev, weekDay))}
              />
              {label}
            </label>
          ))}
        </div>
      </InputWithLabel>
      <InputWithLabel label={"Orari di inizio (fasce del gruppo)"}>
        <div className={"flex flex-wrap gap-2"}>
          {availableHours.map((h) => (
            <label
              key={h}
              className={
                "flex items-center gap-1 rounded border px-2 py-1 text-sm"
              }
            >
              <input
                type={"checkbox"}
                checked={hours.includes(h)}
                onChange={() => setHours((prev) => toggle(prev, h))}
              />
              {formatHour(h)}
            </label>
          ))}
        </div>
      </InputWithLabel>
      <div className={"grid grid-cols-1 md:grid-cols-3 gap-3"}>
        <InputWithLabel label={"Dal"}>
          <TextInput
            type={"date"}
            value={fromDate}
            min={dayjs().format(dateFormat)}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </InputWithLabel>
        <InputWithLabel label={"Al (incluso)"}>
          <TextInput
            type={"date"}
            value={toDate}
            min={fromDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </InputWithLabel>
        <InputWithLabel label={"Conduttore"}>
          <ConductorSelect value={teacherId} onChange={setTeacherId} />
        </InputWithLabel>
      </div>
      <div className={"flex items-center justify-between gap-3"}>
        <p className={"text-sm text-slate-600"}>
          Verranno aggiunti{" "}
          <span className={"font-semibold"}>{slotsCount}</span> slot (quelli già
          presenti nella pianificazione vengono ignorati)
        </p>
        <PrimaryButton
          label={"Aggiungi slot"}
          loading={adding}
          disabled={slotsCount <= 0}
          onClick={add}
        />
      </div>
    </div>
  );
}

export function AddOnboardingLiveSlotsCard({
  availability,
  addSlots,
  addRecurringSlots,
}: {
  availability?: UserGroupAvailability | null;
  addSlots: (
    slots: { startUtc: string; teacherId?: string }[],
  ) => Promise<void>;
  addRecurringSlots: (recurrence: {
    weekDays: WeekDayEnum[];
    startTimes: string[];
    fromDate: string;
    toDate: string;
    teacherId?: string;
  }) => Promise<void>;
}) {
  const [mode, setMode] = useState(AddSlotsMode.Single);
  const showToast = useToast();

  async function withErrorToast(action: () => Promise<void>) {
    try {
      await action();
    } catch (e) {
      showToast((e as ApolloError)?.message ?? "Errore", "error");
    }
  }

  return (
    <Card>
      <div className={"space-y-4"}>
        <div
          className={
            "flex flex-col md:flex-row md:items-center md:justify-between gap-2"
          }
        >
          <h2 className={"font-bold text-lg"}>Aggiungi slot</h2>
          <SegmentedControl
            options={[
              { value: AddSlotsMode.Single, label: "Slot singolo" },
              { value: AddSlotsMode.Recurring, label: "Ricorrenza" },
            ]}
            value={mode}
            onChange={setMode}
          />
        </div>
        <p className={"text-sm text-slate-600"}>
          {mode === AddSlotsMode.Single
            ? "Aggiungi una live in un giorno e orario precisi."
            : "Aggiungi una live per ogni combinazione di giorno della settimana e orario nel periodo scelto. Potrai poi modificare o togliere ogni singolo slot."}
        </p>
        {mode === AddSlotsMode.Single ? (
          <SingleSlotForm
            availability={availability}
            onAdd={(slot) => withErrorToast(() => addSlots([slot]))}
          />
        ) : (
          <RecurringSlotsForm
            availability={availability}
            onAdd={(recurrence) =>
              withErrorToast(() => addRecurringSlots(recurrence))
            }
          />
        )}
      </div>
    </Card>
  );
}
